/**
 * Color Generation Utilities
 * Génère des palettes de couleurs complètes à partir d'une couleur de base
 * avec contrôle de l'écart de décalage et de la balance de blanc
 */

export interface HSL {
  h: number; // 0-360
  s: number; // 0-100
  l: number; // 0-100
}

export interface ColorPalette {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
}

/**
 * Convert hex color to HSL
 */
export const hexToHSL = (hex: string): HSL => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 100, l: 50 };

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
};

/**
 * Convert HSL to hex
 */
export const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x;
  }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Apply white balance adjustment to HSL color
 * @param hsl - Color in HSL format
 * @param whiteBalance - Balance value from -100 (warmer) to +100 (cooler)
 * @returns Adjusted HSL color
 */
export const applyWhiteBalance = (hsl: HSL, whiteBalance: number): HSL => {
  let { h, s, l } = hsl;

  if (whiteBalance > 0) {
    // Cooler (shift toward blue)
    const blueShift = whiteBalance / 100;
    h = (h + (240 - h) * blueShift * 0.3) % 360;
    s = Math.max(0, Math.min(100, s - blueShift * 10));
  } else if (whiteBalance < 0) {
    // Warmer (shift toward orange/yellow)
    const warmShift = Math.abs(whiteBalance) / 100;
    h = (h + (30 - h) * warmShift * 0.3 + 360) % 360;
    s = Math.max(0, Math.min(100, s + warmShift * 5));
  }

  return { h, s, l };
};

/**
 * Generate a complete color palette from a base color
 * @param baseColor - Hex color string (e.g., "#3498db")
 * @param offset - Offset value from -100 to +100 (affects lightness variation)
 * @param whiteBalance - White balance from -100 (warm) to +100 (cool)
 * @returns ColorPalette object with shades 50-900
 */
export const generatePaletteFromBase = (
  baseColor: string,
  offset: number = 0,
  whiteBalance: number = 0
): ColorPalette => {
  const baseHSL = hexToHSL(baseColor);

  // Apply white balance to base color
  const adjustedBase = applyWhiteBalance(baseHSL, whiteBalance);

  // Offset affects the spacing between lightness levels
  // Positive offset = more contrast, Negative offset = less contrast
  const offsetFactor = 1 + (offset / 100);

  // Define lightness levels for each shade (500 is the base)
  const lightnessLevels = {
    50: 95,
    100: 90,
    200: 80,
    300: 70,
    400: 60,
    500: 50, // Base level
    600: 40,
    700: 30,
    800: 20,
    900: 10,
  };

  const palette: Partial<ColorPalette> = {};

  Object.entries(lightnessLevels).forEach(([shade, targetLightness]) => {
    // Calculate lightness with offset adjustment
    const baseLightness = lightnessLevels[500];
    const difference = targetLightness - baseLightness;
    const adjustedDifference = difference * offsetFactor;
    let finalLightness = baseLightness + adjustedDifference;

    // Clamp lightness between 0 and 100
    finalLightness = Math.max(0, Math.min(100, finalLightness));

    // Adjust saturation based on lightness (more natural looking)
    let adjustedSaturation = adjustedBase.s;

    if (finalLightness > 80) {
      // Desaturate very light colors
      const desaturationFactor = (finalLightness - 80) / 20;
      adjustedSaturation = adjustedBase.s * (1 - desaturationFactor * 0.4);
    } else if (finalLightness < 20) {
      // Slightly desaturate very dark colors
      const desaturationFactor = (20 - finalLightness) / 20;
      adjustedSaturation = adjustedBase.s * (1 - desaturationFactor * 0.2);
    }

    // Generate hex color
    const hexColor = hslToHex(
      adjustedBase.h,
      Math.max(0, Math.min(100, adjustedSaturation)),
      finalLightness
    );

    (palette as Record<string, string>)[shade] = hexColor;
  });

  return palette as ColorPalette;
};

/**
 * Generate complementary color
 */
export const getComplementaryColor = (hex: string): string => {
  const hsl = hexToHSL(hex);
  const complementaryHue = (hsl.h + 180) % 360;
  return hslToHex(complementaryHue, hsl.s, hsl.l);
};

/**
 * Generate analogous colors
 */
export const getAnalogousColors = (hex: string): [string, string] => {
  const hsl = hexToHSL(hex);
  const analogous1 = hslToHex((hsl.h + 30) % 360, hsl.s, hsl.l);
  const analogous2 = hslToHex((hsl.h - 30 + 360) % 360, hsl.s, hsl.l);
  return [analogous1, analogous2];
};

/**
 * Generate triadic colors
 */
export const getTriadicColors = (hex: string): [string, string] => {
  const hsl = hexToHSL(hex);
  const triadic1 = hslToHex((hsl.h + 120) % 360, hsl.s, hsl.l);
  const triadic2 = hslToHex((hsl.h + 240) % 360, hsl.s, hsl.l);
  return [triadic1, triadic2];
};

/**
 * Calculate contrast ratio between two colors (WCAG)
 */
export const getContrastRatio = (color1: string, color2: string): number => {
  const getLuminance = (hex: string): number => {
    const rgb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!rgb) return 0;

    const r = parseInt(rgb[1], 16) / 255;
    const g = parseInt(rgb[2], 16) / 255;
    const b = parseInt(rgb[3], 16) / 255;

    const [rs, gs, bs] = [r, g, b].map(val =>
      val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
    );

    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };

  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Check if color meets WCAG AA standards
 */
export const meetsWCAG_AA = (foreground: string, background: string): boolean => {
  const ratio = getContrastRatio(foreground, background);
  return ratio >= 4.5;
};

/**
 * Check if color meets WCAG AAA standards
 */
export const meetsWCAG_AAA = (foreground: string, background: string): boolean => {
  const ratio = getContrastRatio(foreground, background);
  return ratio >= 7;
};

/**
 * Preview palette generation with real-time updates
 */
export const previewPalette = (
  baseColor: string,
  offset: number,
  whiteBalance: number
): {
  palette: ColorPalette;
  complementary: string;
  analogous: [string, string];
  triadic: [string, string];
} => {
  const palette = generatePaletteFromBase(baseColor, offset, whiteBalance);
  const complementary = getComplementaryColor(baseColor);
  const analogous = getAnalogousColors(baseColor);
  const triadic = getTriadicColors(baseColor);

  return {
    palette,
    complementary,
    analogous,
    triadic,
  };
};

